import { useState, useEffect, useRef } from "react";
import { useApp, api, timeAgo } from "../context";
import { Shell, Btn, Stat, Toggle, EmptyState, TierBadge } from "../components";

export const PremiumPage = ({ mpesaPhone, setMpesaPhone, mpesaPlan, setMpesaPlan, mpesaLoading, setMpesaLoading }) => {
  const { c, dark, user, flash } = useApp();
  const [paymentSent, setPaymentSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const initiateMpesa = async (plan) => {
    if (!mpesaPhone.trim()) { flash("Enter your M-Pesa phone number", "error"); return; }
    let phone = mpesaPhone.trim().replace(/\s/g, "");
    if (phone.startsWith("+254")) phone = phone.replace("+254", "254");
    else if (phone.startsWith("0")) phone = "254" + phone.slice(1);
    else if (!phone.startsWith("254")) phone = "254" + phone;
    if (!/^2547[0-9]{8}$/.test(phone)) { flash("Invalid number. Use 07XX or 254XX format", "error"); return; }
    setMpesaPhone(phone);
    setMpesaLoading(true);
    const res = await api("/payments/initiate", { method: "POST", body: JSON.stringify({ phone, plan }) });
    setMpesaLoading(false);
    if (res.ok) {
      setPaymentSent(true);
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => { if (prev <= 1) { clearInterval(timer); setPaymentSent(false); return 0; } return prev - 1; });
      }, 1000);
    } else {
      const msg = res.message || "";
      if (msg.includes("Invalid PhoneNumber")) flash("❌ Invalid phone. Try 07XX format", "error");
      else if (msg.includes("insufficient")) flash("❌ Insufficient M-Pesa balance", "error");
      else if (msg.includes("Cancelled")) flash("❌ Payment cancelled. Try again.", "error");
      else flash(msg || "Payment failed. Please try again.", "error");
    }
  };

  const currentPlan = user?.plan || "free";

  const tiers = [
    {
      key: "free", name: "Basic", icon: "🆓", price: "Free", period: "",
      tagline: "Get started and explore",
      color: c.sub, bg: "transparent", border: c.border,
      features: [
        { text: "Browse all posts & topics", active: true },
        { text: "5 posts per day", active: true },
        { text: "Follow & message free users", active: true },
        { text: "Basic profile with avatar", active: true },
        { text: "3 meetup interests/month (verified)", active: true },
        { text: "AI matching access", active: false },
        { text: "Movie streaming", active: false },
        { text: "Message premium/elite users", active: false },
      ],
    },
    {
      key: "premium", name: "Premium", icon: "💎", price: "KES 499", period: "/month",
      tagline: "3x More Visibility & Connections",
      color: "#7c6cff", bg: dark ? "#7c6cff08" : "#7c6cff05", border: "#7c6cff",
      ribbon: "MOST POPULAR",
      features: [
        { text: "Everything in Basic", active: true, bold: true },
        { text: "Unlimited posts per day", active: true },
        { text: "Premium verified badge", active: true },
        { text: "Posts highlighted in feed", active: true },
        { text: "Follow & message free + premium users", active: true },
        { text: "Full movie streaming access", active: true },
        { text: "Unlimited meetup interests", active: true },
        { text: "AI matching with preferences", active: true },
        { text: "Priority placement in feed", active: true },
      ],
    },
    {
      key: "elite", name: "Elite", icon: "👑", price: "KES 999", period: "/month",
      tagline: "Maximum Visibility. Maximum Power.",
      color: "#f59e0b", bg: dark ? "#f59e0b08" : "#f59e0b05", border: "#f59e0b",
      ribbon: "BEST VALUE",
      features: [
        { text: "Everything in Premium", active: true, bold: true },
        { text: "Elite gold crown badge", active: true },
        { text: "Follow & message everyone", active: true },
        { text: "Gold highlighted posts", active: true },
        { text: "Featured on Discover page", active: true },
        { text: "Top priority in AI matching", active: true },
        { text: "Event ticket discounts", active: true },
        { text: "Direct admin WhatsApp support", active: true },
        { text: "Pin 1 post per week (24hrs)", active: true },
        { text: "Early access to new features", active: true },
      ],
    },
  ];

  return (
    <Shell title="Upgrade" showBack>
      <div style={{ padding: "16px 0" }}>
        <div style={{ background: "linear-gradient(135deg, #6c5ce7 0%, #7c6cff 50%, #f59e0b 100%)", borderRadius: 20, padding: "32px 22px", textAlign: "center", marginBottom: 20, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 60%)" }} />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>💎</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#fff", margin: "0 0 6px" }}>Unlock Your Full Potential</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", margin: 0, lineHeight: 1.5 }}>More visibility. More connections. More power.</p>
          </div>
        </div>

        {currentPlan !== "free" && (
          <div style={{ background: currentPlan === "elite" ? "#f59e0b12" : "#7c6cff12", borderRadius: 12, padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <span style={{ fontSize: 14 }}>{currentPlan === "elite" ? "👑" : "✔"}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: currentPlan === "elite" ? "#f59e0b" : "#7c6cff" }}>You're on {currentPlan === "elite" ? "Elite" : "Premium"}</span>
          </div>
        )}

        {tiers.map((tier) => {
          const isCurrent = currentPlan === tier.key;
          const isUpgrade = tier.key === "premium" ? currentPlan === "free" : tier.key === "elite" ? currentPlan !== "elite" : false;
          const elevated = tier.key === "premium" || tier.key === "elite";
          return (
            <div key={tier.key} style={{ background: tier.bg || c.card, borderRadius: elevated ? 18 : 14, border: isCurrent ? `2px solid ${tier.color}` : elevated ? `1.5px solid ${tier.color}40` : `1px solid ${c.border}`, padding: elevated ? "24px 20px" : "18px 16px", marginBottom: 12, position: "relative", boxShadow: tier.key === "elite" ? "0 4px 20px rgba(245,158,11,0.1)" : tier.key === "premium" ? "0 4px 16px rgba(124,108,255,0.08)" : "none" }}>
              {tier.ribbon && (
                <div style={{ position: "absolute", top: -1, right: 16, padding: "4px 12px", background: tier.key === "elite" ? "linear-gradient(135deg, #f59e0b, #d97706)" : "linear-gradient(135deg, #6c5ce7, #7c6cff)", borderRadius: "0 0 8px 8px", fontSize: 9, fontWeight: 800, color: "#fff", letterSpacing: "0.05em" }}>{tier.ribbon}</div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: tier.key === "free" ? 8 : 12 }}>
                <span style={{ fontSize: tier.key === "elite" ? 28 : 22 }}>{tier.icon}</span>
                <div>
                  <div style={{ fontSize: elevated ? 19 : 15, fontWeight: 800, color: tier.key === "elite" ? "#f59e0b" : tier.key === "premium" ? "#7c6cff" : c.sub }}>{tier.name}</div>
                  {elevated && <div style={{ fontSize: 11, color: c.sub, fontWeight: 600, marginTop: 1 }}>{tier.tagline}</div>}
                </div>
              </div>
              <div style={{ marginBottom: elevated ? 16 : 10 }}>
                <span style={{ fontSize: elevated ? 30 : 22, fontWeight: 800, color: tier.key === "elite" ? "#f59e0b" : c.text }}>{tier.price}</span>
                <span style={{ fontSize: 13, color: c.sub, fontWeight: 600 }}>{tier.period}</span>
              </div>
              <div style={{ marginBottom: elevated ? 18 : 10 }}>
                {tier.features.map((f, i) => {
                  const txt = typeof f === "object" ? f.text : f;
                  const active = typeof f === "object" ? f.active !== false : true;
                  const bold = typeof f === "object" && f.bold;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 0", borderBottom: i < tier.features.length - 1 ? `1px solid ${c.border}15` : "none" }}>
                      <div style={{ width: 20, height: 20, borderRadius: 10, background: active ? `${tier.color}15` : `${c.border}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {active ? <span style={{ fontSize: 10, color: tier.color, fontWeight: 800 }}>✓</span> : <span style={{ fontSize: 9, color: c.faint }}>✕</span>}
                      </div>
                      <span style={{ fontSize: 12.5, color: active ? c.text : c.faint, fontWeight: bold ? 700 : 500, textDecoration: active ? "none" : "line-through" }}>{txt}</span>
                    </div>
                  );
                })}
              </div>
              {isCurrent ? (
                <div style={{ padding: "10px 0", textAlign: "center", borderRadius: 12, background: `${tier.color}15`, color: tier.color, fontSize: 13, fontWeight: 700 }}>✓ Current Plan</div>
              ) : isUpgrade ? (
                mpesaPlan === tier.key ? (
                  <div>
                    {paymentSent ? (
                      <div style={{ background: "#10b98112", borderRadius: 12, padding: "16px 14px", marginBottom: 8, textAlign: "center", border: "1px solid #10b98130" }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>📱</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#10b981", marginBottom: 4 }}>STK Push Sent!</div>
                        <div style={{ fontSize: 12, color: c.sub, marginBottom: 10 }}>Check your phone and enter your M-Pesa PIN</div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: c.text, marginBottom: 8 }}>{countdown}s</div>
                        <div style={{ height: 4, borderRadius: 2, background: c.border, marginBottom: 12 }}>
                          <div style={{ height: 4, borderRadius: 2, background: "#10b981", width: `${(countdown / 60) * 100}%`, transition: "width 1s" }} />
                        </div>
                        <div style={{ fontSize: 11, color: c.faint, marginBottom: 10 }}>Didn't get the prompt?</div>
                        <button onClick={() => { setPaymentSent(false); }} style={{ padding: "8px 20px", borderRadius: 10, border: `1px solid ${c.border}`, background: "transparent", color: c.sub, fontSize: 12, fontWeight: 600, cursor: "pointer", marginRight: 8 }}>Try Again</button>
                        <button onClick={() => { setPaymentSent(false); setMpesaPlan(null); }} style={{ padding: "8px 20px", borderRadius: 10, border: "none", background: "#ef444415", color: "#ef4444", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        <input
                          style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${tier.color}40`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 6 }}
                          placeholder="07XX XXX XXX or 254XX..."
                          value={mpesaPhone}
                          onChange={e => setMpesaPhone(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && initiateMpesa(tier.key)}
                        />
                        <div style={{ fontSize: 11, color: c.faint, marginBottom: 10 }}>💡 Works with 07XX, 01XX, or 254XX format</div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => initiateMpesa(tier.key)} disabled={mpesaLoading} style={{ flex: 1, padding: "12px 0", borderRadius: 12, border: "none", background: tier.key === "elite" ? "linear-gradient(135deg, #f59e0b, #d97706)" : "linear-gradient(135deg, #6c5ce7, #7c6cff)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: mpesaLoading ? 0.7 : 1 }}>{mpesaLoading ? "⏳ Sending..." : "💳 Pay with M-Pesa"}</button>
                          <button onClick={() => setMpesaPlan(null)} style={{ padding: "12px 16px", borderRadius: 12, border: `1px solid ${c.border}`, background: c.card, color: c.sub, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <button onClick={() => setMpesaPlan(tier.key)} style={{ width: "100%", padding: "13px 0", borderRadius: 12, border: "none", background: tier.key === "elite" ? "linear-gradient(135deg, #f59e0b, #d97706)" : "linear-gradient(135deg, #6c5ce7, #7c6cff)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", boxShadow: tier.key === "elite" ? "0 4px 16px rgba(245,158,11,0.25)" : "0 4px 12px rgba(108,92,231,0.2)" }}>
                    {tier.key === "elite" ? "👑 Upgrade to Elite" : "💎 Upgrade to Premium"}
                  </button>
                )
              ) : null}
            </div>
          );
        })}

        <div style={{ background: dark ? "#1a1525" : "#fef9f0", borderRadius: 16, border: `1px solid ${dark ? "#2d2340" : "#f5e6cc"}`, padding: "18px 16px", marginTop: 8, textAlign: "center" }}>
          <div style={{ fontSize: 22, marginBottom: 6 }}>🎬</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 4 }}>Movie Night Coming Soon!</div>
          <div style={{ fontSize: 12, color: c.sub, lineHeight: 1.5, marginBottom: 12 }}>Premium & Elite members get exclusive access to watch parties, live chat, and VIP lounges.</div>
          {currentPlan === "free" && (
            <button onClick={() => setMpesaPlan("premium")} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #6c5ce7, #7c6cff)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Upgrade Now to Get Access</button>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 20, padding: "18px 0 8px", color: c.faint, fontSize: 11 }}>
          <span>👑 7 Elite members</span>
          <span>💎 15 Premium members</span>
          <span>👥 277 total members</span>
        </div>
      </div>
    </Shell>
  );
};



const AdminEvents = () => {
  const { c, flash } = useApp();
  const [events, setEvents] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [viewTickets, setViewTickets] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [verifyNum, setVerifyNum] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [eventImage, setEventImage] = useState(null);
  const [eventImagePreview, setEventImagePreview] = useState(null);
  const eventFileRef = useRef(null);
  const [form, setForm] = useState({ title: "", description: "", venue: "", location: "", event_date: "", event_time: "", image_url: "", ticket_types: [{ name: "Early Bird", price: 500, total_tickets: 50 }, { name: "Regular", price: 1000, total_tickets: 100 }, { name: "VIP", price: 2500, total_tickets: 20 }] });

  const load = () => api("/admin/events/admin/list").then(r => { if (r.ok) setEvents(r.data || []); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.venue || !form.location || !form.event_date) { flash("Fill in title, venue, location and date", "error"); return; }
    const validTypes = form.ticket_types.filter(t => t.name && t.price >= 0 && t.total_tickets > 0);
    if (validTypes.length === 0) { flash("Add at least 1 ticket type", "error"); return; }
    const fd = new FormData();
    fd.append("title", form.title); fd.append("description", form.description);
    fd.append("venue", form.venue); fd.append("location", form.location);
    fd.append("event_date", form.event_date); fd.append("event_time", form.event_time);
    if (eventImage) fd.append("image", eventImage);
    else if (form.image_url) fd.append("image_url", form.image_url);
    validTypes.forEach((tt, i) => { fd.append(`ticket_types[${i}][name]`, tt.name); fd.append(`ticket_types[${i}][price]`, tt.price); fd.append(`ticket_types[${i}][total_tickets]`, tt.total_tickets); });
    const token = localStorage.getItem("gc_token");
    const r = await fetch("/api/admin/events/admin/create", { method: "POST", headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }, body: fd }).then(async res => ({ ok: res.ok, data: await res.json().catch(() => null) }));
    if (r.ok) { flash("Event created!"); setShowAdd(false); load(); }
    else flash(r.data?.message || "Failed", "error");
  };

  const updateStatus = async (id, status) => {
    const r = await api(`/admin/events/admin/${id}/status`, { method: "POST", body: JSON.stringify({ status }) });
    if (r.ok) { flash("Status updated"); load(); }
  };

  const loadTickets = async (id) => {
    setViewTickets(id);
    const r = await api(`/admin/events/admin/${id}/tickets`);
    if (r.ok) setTickets(r.data || []);
  };

  const doVerify = async () => {
    if (!verifyNum) return;
    const r = await api("/admin/events/admin/verify-ticket", { method: "POST", body: JSON.stringify({ ticket_number: verifyNum }) });
    if (r.ok) setVerifyResult(r.data);
    else { setVerifyResult(null); flash("Ticket not found", "error"); }
  };

  const markVerified = async (ticketId) => {
    const r = await api(`/admin/events/admin/tickets/${ticketId}/mark-verified`, { method: "POST" });
    if (r.ok) { flash("Ticket verified!"); if (viewTickets) loadTickets(viewTickets); doVerify(); }
  };

  const S = { inp: { width: "100%", padding: "9px 10px", borderRadius: 10, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 12, outline: "none", boxSizing: "border-box", marginBottom: 8 } };

  const updateTicketType = (idx, field, val) => {
    const tt = [...form.ticket_types];
    tt[idx] = { ...tt[idx], [field]: field === "name" ? val : parseInt(val) || 0 };
    setForm({ ...form, ticket_types: tt });
  };

  if (viewTickets) return (
    <div style={{ background: c.card, borderRadius: 15, border: `1px solid ${c.border}`, padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: c.text }}>Event Tickets ({tickets.length})</h3>
        <button onClick={() => setViewTickets(null)} style={{ background: "none", border: "none", color: c.sub, cursor: "pointer", fontSize: 14 }}>✕ Back</button>
      </div>
      {tickets.map(t => (
        <div key={t.id} style={{ padding: "10px 0", borderBottom: `1px solid ${c.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: c.text }}>{t.user?.name} — <span style={{ fontFamily: "monospace", color: c.pri }}>{t.ticket_number}</span></div>
            <div style={{ fontSize: 10, color: c.faint }}>{t.ticket_type?.name} · KES {t.amount_paid} · {t.phone} · {t.payment_status}</div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color: t.is_verified ? "#10b981" : "#f59e0b" }}>{t.is_verified ? "✓ Verified" : "Unverified"}</span>
        </div>
      ))}
      {tickets.length === 0 && <EmptyState icon="🎫" title="No tickets sold" />}
    </div>
  );

  return (
    <div style={{ background: c.card, borderRadius: 15, border: `1px solid ${c.border}`, padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: c.text }}>Events ({events.length})</h3>
        <button onClick={() => setShowAdd(!showAdd)} style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: c.pri, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{showAdd ? "Cancel" : "+ Create Event"}</button>
      </div>

      {/* Ticket Verifier */}
      <div style={{ background: c.input, borderRadius: 12, padding: 14, marginBottom: 14, border: `1px solid ${c.border}` }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: c.text, marginBottom: 6 }}>🔍 Verify Ticket</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input value={verifyNum} onChange={e => setVerifyNum(e.target.value.toUpperCase())} placeholder="Enter ticket number (GE-XXXXXXXX)" style={{ ...S.inp, flex: 1, marginBottom: 0, fontFamily: "monospace", textTransform: "uppercase" }} />
          <button onClick={doVerify} style={{ padding: "8px 14px", borderRadius: 10, border: "none", background: c.pri, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>Verify</button>
        </div>
        {verifyResult && (
          <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: verifyResult.payment_status === "completed" ? "#10b98108" : "#ef444408", border: `1px solid ${verifyResult.payment_status === "completed" ? "#10b98130" : "#ef444430"}` }}>
            {[{ l: "Ticket", v: verifyResult.ticket_number }, { l: "Name", v: verifyResult.user_name }, { l: "Phone", v: verifyResult.phone }, { l: "M-Pesa ID", v: verifyResult.mpesa_transaction_id || "Pending" }, { l: "Amount", v: `KES ${verifyResult.amount_paid}` }, { l: "Type", v: verifyResult.ticket_type }, { l: "Event", v: verifyResult.event }, { l: "Payment", v: verifyResult.payment_status }, { l: "Verified", v: verifyResult.is_verified ? `Yes (${verifyResult.verified_at})` : "Not yet" }].map((r, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 11 }}>
                <span style={{ color: c.sub, fontWeight: 600 }}>{r.l}</span>
                <span style={{ color: c.text, fontWeight: 700 }}>{r.v}</span>
              </div>
            ))}
            {!verifyResult.is_verified && verifyResult.payment_status === "completed" && (
              <button onClick={() => markVerified(verifyResult.ticket_id)} style={{ width: "100%", marginTop: 8, padding: "7px 0", borderRadius: 8, border: "none", background: "#10b98118", color: "#10b981", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✓ Mark as Verified (Entry Confirmed)</button>
            )}
          </div>
        )}
      </div>

      {/* Create form */}
      {showAdd && (
        <div style={{ background: c.input, borderRadius: 12, padding: 14, marginBottom: 14, border: `1px solid ${c.border}` }}>
          <input placeholder="Event title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={S.inp} />
          <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...S.inp, height: 60, resize: "none" }} />
          <div style={{ display: "flex", gap: 6 }}>
            <input placeholder="Venue name" value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })} style={{ ...S.inp, flex: 1 }} />
            <select value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} style={{ ...S.inp, flex: 1 }}>
              <option value="">County</option>{"Nairobi,Mombasa,Kisumu,Nakuru,Kiambu,Kajiado,Kilifi,Machakos".split(",").map(co => <option key={co} value={co}>{co}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input type="datetime-local" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} style={{ ...S.inp, flex: 1 }} />
            <input placeholder="Time e.g. 8PM" value={form.event_time} onChange={e => setForm({ ...form, event_time: e.target.value })} style={{ ...S.inp, flex: 1 }} />
          </div>
          <div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={() => eventFileRef.current?.click()} style={{ padding: "10px 16px", borderRadius: 10, border: `1.5px dashed ${c.pri}`, background: c.priBg, color: c.pri, fontSize: 12, fontWeight: 700, cursor: "pointer", flex: 1 }}>{eventImage ? "📷 Change Image" : "📷 Upload Event Poster"}</button>
              <input ref={eventFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const file = e.target.files[0]; if (file) { if (file.size > 5 * 1024 * 1024) { flash("Max 5MB", "error"); return; } setEventImage(file); const reader = new FileReader(); reader.onload = ev => setEventImagePreview(ev.target.result); reader.readAsDataURL(file); } }} />
              {eventImagePreview && <button onClick={() => { setEventImage(null); setEventImagePreview(null); }} style={{ padding: "8px 12px", borderRadius: 8, border: "none", background: "#ef444412", color: "#ef4444", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕</button>}
            </div>
            {eventImagePreview && <div style={{ marginTop: 8, borderRadius: 10, overflow: "hidden", height: 120 }}><img src={eventImagePreview} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="preview" /></div>}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: c.text, margin: "6px 0 8px" }}>Ticket Types</div>
          {form.ticket_types.map((tt, i) => (
            <div key={i} style={{ display: "flex", gap: 4, marginBottom: 6, alignItems: "center" }}>
              <input placeholder="Name" value={tt.name} onChange={e => updateTicketType(i, "name", e.target.value)} style={{ ...S.inp, flex: 2, marginBottom: 0 }} />
              <input type="number" placeholder="Price" value={tt.price} onChange={e => updateTicketType(i, "price", e.target.value)} style={{ ...S.inp, flex: 1, marginBottom: 0 }} />
              <input type="number" placeholder="Qty" value={tt.total_tickets} onChange={e => updateTicketType(i, "total_tickets", e.target.value)} style={{ ...S.inp, flex: 1, marginBottom: 0 }} />
              <button onClick={() => setForm({ ...form, ticket_types: form.ticket_types.filter((_, idx) => idx !== i) })} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14 }}>✕</button>
            </div>
          ))}
          <button onClick={() => setForm({ ...form, ticket_types: [...form.ticket_types, { name: "", price: 0, total_tickets: 50 }] })} style={{ width: "100%", padding: "6px 0", borderRadius: 8, border: `1px dashed ${c.border}`, background: "transparent", color: c.sub, fontSize: 11, fontWeight: 600, cursor: "pointer", marginBottom: 10 }}>+ Add Ticket Type</button>
          <Btn primary full onClick={save}>Create Event</Btn>
        </div>
      )}

      {/* Event list */}
      {events.length === 0 ? <EmptyState icon="🎉" title="No events yet" desc="Create your first event" /> :
        events.map(ev => (
          <div key={ev.id} style={{ padding: "12px 0", borderBottom: `1px solid ${c.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{ev.title}
                  <span style={{ marginLeft: 6, fontSize: 10, padding: "2px 6px", borderRadius: 6, fontWeight: 600, background: ev.status === "upcoming" ? "#6c5ce715" : ev.status === "completed" ? "#10b98115" : "#f59e0b15", color: ev.status === "upcoming" ? "#6c5ce7" : ev.status === "completed" ? "#10b981" : "#f59e0b" }}>{ev.status}</span>
                </div>
                <div style={{ fontSize: 10, color: c.faint }}>{ev.venue} · {new Date(ev.event_date).toLocaleDateString()} · {ev.tickets_count} tickets sold</div>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => loadTickets(ev.id)} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: c.priBg, color: c.priTxt, fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Tickets</button>
                {ev.status === "upcoming" && <button onClick={() => updateStatus(ev.id, "completed")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#10b98118", color: "#10b981", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Complete</button>}
                {ev.status === "upcoming" && <button onClick={() => updateStatus(ev.id, "cancelled")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#ef444412", color: "#ef4444", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Cancel</button>}
              </div>
            </div>
            {ev.ticket_types?.map(tt => (
              <div key={tt.id} style={{ fontSize: 10, color: c.faint, marginTop: 3 }}>{tt.name}: KES {tt.price} · {tt.sold_count}/{tt.total_tickets} sold</div>
            ))}
          </div>
        ))
      }
    </div>
  );
};

const AdminMovies = () => {
  const { c, flash } = useApp();
  const [movies, setMovies] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const vidRef = useRef(null);
  const [form, setForm] = useState({ title: "", description: "", category: "Romance", video_url: "", thumbnail_url: "", status: "draft", duration_minutes: "" });

  const load = () => api("/admin/movies/admin/list").then(r => { if (r.ok) setMovies(r.data || []); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || (!form.video_url && !videoFile)) { flash("Title and video required", "error"); return; }
    let r;
    if (videoFile) {
      const fd = new FormData();
      fd.append("title", form.title); fd.append("description", form.description);
      fd.append("category", form.category); fd.append("status", form.status);
      if (form.duration_minutes) fd.append("duration_minutes", form.duration_minutes);
      fd.append("video_file", videoFile);
      if (form.thumbnail_url) fd.append("thumbnail_url", form.thumbnail_url);
      const tk = localStorage.getItem("gc_token");
      setUploading(true); setUploadPct(0);
      r = await new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) setUploadPct(Math.round(e.loaded / e.total * 100)); };
        xhr.onload = () => { setUploading(false); setUploadPct(0); try { resolve({ ok: xhr.status >= 200 && xhr.status < 300, data: JSON.parse(xhr.responseText) }); } catch { resolve({ ok: false }); } };
        xhr.onerror = () => { setUploading(false); setUploadPct(0); resolve({ ok: false }); };
        xhr.open("POST", "/api/admin/movies/admin/add");
        xhr.setRequestHeader("Authorization", "Bearer " + tk);
        xhr.setRequestHeader("Accept", "application/json");
        xhr.send(fd);
      });
    } else {
      r = await api("/admin/movies/admin/add", { method: "POST", body: JSON.stringify({ ...form, duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null }) });
    }
    if (r.ok) { flash("Movie added!"); setVideoFile(null); setShowAdd(false); setForm({ title: "", description: "", category: "Romance", video_url: "", thumbnail_url: "", status: "draft", duration_minutes: "" }); load(); }
    else flash(r.data?.message || "Failed", "error");
  };

  const updateStatus = async (id, status) => {
    const r = await api(`/admin/movies/admin/${id}`, { method: "PUT", body: JSON.stringify({ status }) });
    if (r.ok) { flash(`Movie ${status === "live" ? "is now LIVE!" : status === "replay" ? "moved to replays" : "set to draft"}`); load(); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this movie?")) return;
    const r = await api(`/admin/movies/admin/${id}`, { method: "DELETE" });
    if (r.ok) { flash("Deleted"); load(); }
  };

  const S = { inp: { width: "100%", padding: "9px 10px", borderRadius: 10, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 12, outline: "none", boxSizing: "border-box", marginBottom: 8 } };

  return (
    <div style={{ background: c.card, borderRadius: 15, border: `1px solid ${c.border}`, padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: c.text }}>Movies ({movies.length})</h3>
        <button onClick={() => setShowAdd(!showAdd)} style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: c.pri, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{showAdd ? "Cancel" : "+ Add Movie"}</button>
      </div>
      {showAdd && (
        <div style={{ background: c.input, borderRadius: 12, padding: 14, marginBottom: 14, border: `1px solid ${c.border}` }}>
          <input placeholder="Movie title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={S.inp} />
          <textarea placeholder="Description (optional)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...S.inp, height: 60, resize: "none" }} />
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <button onClick={() => vidRef.current?.click()} type="button" style={{ flex: 1, padding: "10px", borderRadius: 10, border: `1.5px dashed ${videoFile ? "#10b981" : c.pri}`, background: videoFile ? "#10b98108" : "transparent", color: videoFile ? "#10b981" : c.pri, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{videoFile ? "✅ " + videoFile.name.slice(0,20) : "📹 Upload Video"}</button>
              <input ref={vidRef} type="file" accept="video/*" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) setVideoFile(e.target.files[0]); }} />
              {videoFile && <button onClick={() => setVideoFile(null)} type="button" style={{ padding: "8px 12px", borderRadius: 8, border: "none", background: "#ef444412", color: "#ef4444", fontSize: 11, cursor: "pointer" }}>✕</button>}
            </div>
            <input placeholder="Or paste Video URL" value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} style={S.inp} />
          
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={{ ...S.inp, flex: 1 }}>
              {["Romance","Thriller","Drama","Fantasy","Educational","Solo","Couples","Group","Fetish","BDSM","Amateur"].map(c2 => <option key={c2} value={c2}>{c2}</option>)}
            </select>
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={{ ...S.inp, flex: 1 }}>
              <option value="draft">Draft</option><option value="live">Go Live Now</option><option value="replay">Save as Replay</option>
            </select>
          </div>
          <input type="number" placeholder="Duration (minutes)" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} style={S.inp} />
          {uploading && <div style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: c.pri, fontWeight: 700 }}>Uploading video...</span>
                <span style={{ fontSize: 11, color: c.pri, fontWeight: 700 }}>{uploadPct}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: c.border, overflow: "hidden" }}>
                <div style={{ width: uploadPct + "%", height: "100%", borderRadius: 4, background: "linear-gradient(90deg, #6c5ce7, #a23ad1)", transition: "width 0.3s" }} />
              </div>
            </div>}
            <Btn primary full onClick={save} disabled={uploading}>{uploading ? "Uploading..." : "Add Movie"}</Btn>
        </div>
      )}
      {movies.length === 0 ? <EmptyState icon="🎬" title="No movies yet" desc="Add your first movie above" /> :
        movies.map(m => (
          <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${c.border}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{m.title}
                <span style={{ marginLeft: 6, fontSize: 10, padding: "2px 6px", borderRadius: 6, fontWeight: 600, background: m.status === "live" ? "#ef444420" : m.status === "replay" ? "#10b98120" : c.input, color: m.status === "live" ? "#ef4444" : m.status === "replay" ? "#10b981" : c.faint }}>{m.status.toUpperCase()}</span>
              </div>
              <div style={{ fontSize: 11, color: c.faint }}>{m.category} · {m.view_count} views · {m.like_count} likes</div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {m.status !== "live" && <button onClick={() => updateStatus(m.id, "live")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#ef444420", color: "#ef4444", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Go Live</button>}
              {m.status === "live" && <button onClick={() => updateStatus(m.id, "replay")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#10b98120", color: "#10b981", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>End Live</button>}
              {m.status !== "draft" && <button onClick={() => updateStatus(m.id, "draft")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: c.input, color: c.faint, fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Draft</button>}
              <button onClick={() => del(m.id)} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "#ef444410", color: "#ef4444", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
            </div>
          </div>
        ))
      }
    </div>
  );
};


// ─── Admin Sticker Manager ──────────────────────────────────────────────────
const AdminStickers = () => {
  const { c, dark, flash } = useApp();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePackId, setActivePackId] = useState(null);
  const [showNewPack, setShowNewPack] = useState(false);
  const [newPackName, setNewPackName] = useState('');
  const [newPackDesc, setNewPackDesc] = useState('');
  const [newPackTier, setNewPackTier] = useState('premium');
  const [newPackCover, setNewPackCover] = useState(null);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  useEffect(() => { loadPacks(); }, []);

  const loadPacks = async () => {
    setLoading(true);
    const res = await api('/stickers/packs');
    if (res.ok) { setPacks(res.data); if (res.data.length > 0 && !activePackId) setActivePackId(res.data[0].id); }
    else if (res.status === 403) { setPacks([]); }
    setLoading(false);
  };

  const createPack = async () => {
    if (!newPackName.trim()) { flash('Pack name required', 'error'); return; }
    setCreating(true);
    const fd = new FormData();
    fd.append('name', newPackName.trim());
    fd.append('description', newPackDesc.trim());
    fd.append('tier', newPackTier);
    if (newPackCover) fd.append('cover_image', newPackCover);
    const token = localStorage.getItem('gc_token');
    const res = await fetch('/api/stickers/packs', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token, 'Accept': 'application/json' },
      body: fd
    });
    const data = await res.json();
    setCreating(false);
    if (res.ok) {
      flash('Pack created! Now upload stickers 🎉');
      setNewPackName(''); setNewPackDesc(''); setNewPackCover(null); setShowNewPack(false);
      await loadPacks();
      setActivePackId(data.id);
    } else flash(data.message || 'Failed to create pack', 'error');
  };

  const uploadStickers = async (files) => {
    if (!activePackId) { flash('Select a pack first', 'error'); return; }
    if (!files || files.length === 0) return;
    setUploading(true); setUploadPct(0);
    const fd = new FormData();
    fd.append('sticker_pack_id', activePackId);
    Array.from(files).forEach(f => fd.append('stickers[]', f));
    const token = localStorage.getItem('gc_token');
    await new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) setUploadPct(Math.round(e.loaded / e.total * 100)); };
      xhr.onload = async () => {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status === 201) { flash(data.message || 'Stickers uploaded! ✨'); await loadPacks(); }
        else flash(data.message || 'Upload failed', 'error');
        setUploading(false); setUploadPct(0); resolve();
      };
      xhr.onerror = () => { flash('Upload error', 'error'); setUploading(false); resolve(); };
      xhr.open('POST', '/api/stickers/upload');
      xhr.setRequestHeader('Authorization', 'Bearer ' + token);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.send(fd);
    });
  };

  const deletePack = async (id) => {
    if (!window.confirm('Delete this entire pack and all its stickers?')) return;
    const res = await api('/stickers/packs/' + id, { method: 'DELETE' });
    if (res.ok) { flash('Pack deleted'); setActivePackId(null); await loadPacks(); }
    else flash('Failed', 'error');
  };

  const deleteSticker = async (id) => {
    const res = await api('/stickers/' + id, { method: 'DELETE' });
    if (res.ok) { flash('Sticker removed'); await loadPacks(); }
    else flash('Failed', 'error');
  };

  const activePack = packs.find(p => p.id === activePackId);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: c.faint }}>Loading sticker packs...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: c.text }}>🎭 Sticker Packs ({packs.length})</h3>
        <Btn primary small onClick={() => setShowNewPack(p => !p)}>+ New Pack</Btn>
      </div>

      {/* New Pack Form */}
      {showNewPack && (
        <div style={{ background: c.card, borderRadius: 14, border: '1px solid ' + c.border, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 4 }}>Create New Pack</div>
          <input placeholder='Pack name (e.g. Flirty, Reactions)' value={newPackName} onChange={e => setNewPackName(e.target.value)}
            style={{ padding: '9px 13px', borderRadius: 9, border: '1px solid ' + c.border, background: c.input, color: c.text, fontSize: 13, outline: 'none' }} />
          <input placeholder='Description (optional)' value={newPackDesc} onChange={e => setNewPackDesc(e.target.value)}
            style={{ padding: '9px 13px', borderRadius: 9, border: '1px solid ' + c.border, background: c.input, color: c.text, fontSize: 13, outline: 'none' }} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: c.sub, fontWeight: 600 }}>Who can use:</label>
            <select value={newPackTier} onChange={e => setNewPackTier(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid ' + c.border, background: c.input, color: c.text, fontSize: 12, fontWeight: 600, outline: 'none' }}>
              <option value='free'>Everyone (Free+)</option>
              <option value='premium'>Premium + Elite</option>
              <option value='elite'>Elite Only</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: c.sub, fontWeight: 600 }}>Cover image:</label>
            <input ref={coverInputRef} type='file' accept='image/*' style={{ display: 'none' }} onChange={e => setNewPackCover(e.target.files[0])} />
            <button onClick={() => coverInputRef.current?.click()}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid ' + c.border, background: c.card, color: c.sub, fontSize: 12, cursor: 'pointer' }}>
              {newPackCover ? newPackCover.name : 'Choose image'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn small onClick={() => setShowNewPack(false)}>Cancel</Btn>
            <Btn primary small onClick={createPack} disabled={creating}>{creating ? 'Creating...' : 'Create Pack'}</Btn>
          </div>
        </div>
      )}

      {/* Pack Tabs */}
      {packs.length === 0 && !showNewPack && (
        <div style={{ textAlign: 'center', padding: '40px 20px', background: c.card, borderRadius: 14, border: '1px solid ' + c.border }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🎭</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: c.text, marginBottom: 6 }}>No sticker packs yet</div>
          <div style={{ fontSize: 12, color: c.faint }}>Create your first pack to get started</div>
        </div>
      )}

      {packs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {packs.map(pack => (
            <button key={pack.id} onClick={() => setActivePackId(pack.id)}
              style={{ padding: '7px 14px', borderRadius: 20, border: '2px solid ' + (activePackId === pack.id ? '#e91e8c' : c.border),
                background: activePackId === pack.id ? '#e91e8c15' : c.card,
                color: activePackId === pack.id ? '#e91e8c' : c.sub,
                fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {pack.cover_image_url && <img src={pack.cover_image_url} alt='' style={{ width: 14, height: 14, borderRadius: 3, marginRight: 5, verticalAlign: 'middle' }} />}
              {pack.name}
              <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.7 }}>({pack.stickers?.length || 0})</span>
            </button>
          ))}
        </div>
      )}

      {/* Active Pack Detail */}
      {activePack && (
        <div style={{ background: c.card, borderRadius: 14, border: '1px solid ' + c.border, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.text }}>{activePack.name}</div>
              <div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>{activePack.description || 'No description'} · Tier: <b>{activePack.tier}</b> · {activePack.stickers?.length || 0} stickers</div>
            </div>
            <button onClick={() => deletePack(activePack.id)}
              style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid ' + c.red, background: 'transparent', color: c.red, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🗑 Delete Pack</button>
          </div>

          {/* Upload Drop Zone */}
          <input ref={fileInputRef} type='file' multiple accept='.gif,.png,.webp,.jpg,.jpeg' style={{ display: 'none' }}
            onChange={e => uploadStickers(e.target.files)} />
          <div onClick={() => !uploading && fileInputRef.current?.click()}
            style={{ border: '2px dashed ' + (uploading ? '#e91e8c' : c.border), borderRadius: 12, padding: '20px', textAlign: 'center',
              cursor: uploading ? 'default' : 'pointer', marginBottom: 14, background: uploading ? '#e91e8c08' : 'transparent',
              transition: 'all 0.2s' }}>
            {uploading ? (
              <><div style={{ fontSize: 13, color: '#e91e8c', fontWeight: 700, marginBottom: 8 }}>Uploading... {uploadPct}%</div>
              <div style={{ height: 6, background: c.border, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: uploadPct + '%', background: '#e91e8c', borderRadius: 3, transition: 'width 0.3s' }} /></div></>
            ) : (
              <><div style={{ fontSize: 28, marginBottom: 6 }}>📁</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: c.text }}>Click to upload stickers</div>
              <div style={{ fontSize: 11, color: c.faint, marginTop: 4 }}>GIF, PNG, WebP, JPG · Max 5MB each · Up to 20 at once</div></>
            )}
          </div>

          {/* Sticker Grid */}
          {activePack.stickers?.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: c.faint, fontSize: 13 }}>No stickers yet. Upload some above!</div>
          )}
          {activePack.stickers?.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {activePack.stickers.map(s => (
                <div key={s.id} style={{ position: 'relative', background: c.input, borderRadius: 10, padding: 6, border: '1px solid ' + c.border }}>
                  <img src={s.url} alt={s.name}
                    style={{ width: '100%', height: 70, objectFit: 'contain', borderRadius: 6, display: 'block' }} />
                  <div style={{ fontSize: 10, color: c.faint, textAlign: 'center', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                  <button onClick={() => deleteSticker(s.id)}
                    style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: 10,
                      border: 'none', background: c.red, color: '#fff', fontSize: 10,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>✕</button>
                </div>
              ))}
            </div>
          )}

        </div>
      )}

    </div>
  );
};

export const AdminPage = ({ adminTab, setAdminTab, adminData, adminUsers, adminReports, posts, topic, loadPosts, loadAdminUsers }) => {
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => { if (adminTab === "analytics") api("/admin/analytics").then(r => { if (r.ok) setAnalytics(r.data); }); }, [adminTab]);
  const { c, flash } = useApp();
  const ad = adminData || {};

  // Verification requests state
  const [verifRequests, setVerifRequests] = useState([]);
  const [verifFilter, setVerifFilter] = useState("pending");
  const [rejectNote, setRejectNote] = useState({});

  useEffect(() => {
    if (adminTab === "verify") {
      api(`/admin/verifications?status=${verifFilter}`).then(res => {
        if (res.ok) setVerifRequests(res.data?.data || []);
      });
    }
  }, [adminTab, verifFilter]);

  const approveVerif = async (id) => {
    const res = await api(`/admin/verifications/${id}/approve`, { method: "POST" });
    if (res.ok) {
      flash("User verified! ✓");
      setVerifRequests(prev => prev.filter(v => v.id !== id));
    } else flash(res.message || "Failed", "error");
  };

  const rejectVerif = async (id) => {
    if (!rejectNote[id]?.trim()) { flash("Please add a reason for rejection", "error"); return; }
    const res = await api(`/admin/verifications/${id}/reject`, { method: "POST", body: JSON.stringify({ note: rejectNote[id] }) });
    if (res.ok) {
      flash("Request rejected");
      setVerifRequests(prev => prev.filter(v => v.id !== id));
    } else flash(res.message || "Failed", "error");
  };

  return (
    <Shell title="Admin" showBack>
      <div style={{ padding: "14px 0" }}>
        <div style={{ display: "flex", gap: 3, marginBottom: 16, overflowX: "auto", scrollbarWidth: "none" }}>
          {["overview", "analytics", "users", "verify", "reports", "content", "stickers", "movies", "events", "revenue", "settings"].map(tab => (<button key={tab} onClick={() => setAdminTab(tab)} style={{ padding: "7px 14px", borderRadius: 9, border: "none", whiteSpace: "nowrap", background: adminTab === tab ? c.priBg : "transparent", color: adminTab === tab ? c.priTxt : c.sub, fontSize: 12, fontWeight: 700, cursor: "pointer", textTransform: "capitalize" }}>{tab}</button>))}
        </div>
        {adminTab === "overview" && (<><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 16 }}><Stat icon="👥" label="Total Users" value={ad.total_users || 0} color={c.pri} /><Stat icon="🟢" label="Active Today" value={ad.active_today || 0} color={c.grn} /><Stat icon="📝" label="Total Posts" value={ad.total_posts || 0} color={c.blue} /><Stat icon="📊" label="Posts Today" value={ad.posts_today || 0} color="#8b5cf6" /><Stat icon="⚠️" label="Reports" value={ad.open_reports || 0} color={c.red} /><Stat icon="🆕" label="New Today" value={ad.new_today || 0} color={c.gold} /><Stat icon="👑" label="Premium" value={ad.premium_users || 0} color="#8b5cf6" /><Stat icon="💰" label="Revenue" value={`KES ${(ad.revenue || 0).toLocaleString()}`} color={c.grn} /></div><div style={{ background: c.card, borderRadius: 15, border: `1px solid ${c.border}`, padding: 18 }}><Stat icon="⭐" label="Avg Trust Score" value={ad.avg_trust || 0} color={c.gold} /></div></>)}

        {adminTab === "analytics" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {!analytics ? <div style={{ textAlign: "center", padding: 30, color: c.sub }}>Loading analytics...</div> : <>
              {/* Key Metrics */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {[
                  { label: "Total Users", value: analytics.total_users, color: "#6c5ce7" },
                  { label: "Total Posts", value: analytics.total_posts, color: "#10b981" },
                  { label: "Total DMs", value: analytics.total_messages, color: "#3b82f6" },
                ].map((m, i) => (
                  <div key={i} style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 14, textAlign: "center" }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: m.color }}>{m.value}</div>
                    <div style={{ fontSize: 10, color: c.faint, fontWeight: 600, marginTop: 2 }}>{m.label}</div>
                  </div>
                ))}
              </div>

              {/* Retention */}
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>User Retention (7-day)</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: analytics.retention >= 50 ? "#10b981" : analytics.retention >= 25 ? "#f59e0b" : "#ef4444" }}>{analytics.retention}%</span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: c.border }}>
                  <div style={{ height: 8, borderRadius: 4, background: analytics.retention >= 50 ? "#10b981" : analytics.retention >= 25 ? "#f59e0b" : "#ef4444", width: `${analytics.retention}%`, transition: "width 1s" }} />
                </div>
              </div>

              {/* Plan Breakdown */}
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Membership Breakdown</div>
                {[
                  { label: "Free", count: analytics.plans?.free || 0, color: c.sub },
                  { label: "Premium", count: analytics.plans?.premium || 0, color: "#7c6cff" },
                  { label: "Elite", count: analytics.plans?.elite || 0, color: "#f59e0b" },
                ].map((p, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < 2 ? `1px solid ${c.border}` : "none" }}>
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: p.color, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: c.text }}>{p.label}</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: p.color }}>{p.count}</span>
                    <span style={{ fontSize: 10, color: c.faint, width: 40, textAlign: "right" }}>{analytics.total_users > 0 ? Math.round(p.count / analytics.total_users * 100) : 0}%</span>
                  </div>
                ))}
              </div>

              {/* Verification Stats */}
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Verification</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { label: "Verified", count: analytics.verification?.verified || 0, color: "#10b981" },
                    { label: "Unverified", count: analytics.verification?.unverified || 0, color: "#ef4444" },
                    { label: "Pending", count: analytics.verification?.pending || 0, color: "#f59e0b" },
                  ].map((v, i) => (
                    <div key={i} style={{ flex: 1, background: `${v.color}08`, borderRadius: 10, padding: 12, textAlign: "center", border: `1px solid ${v.color}20` }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: v.color }}>{v.count}</div>
                      <div style={{ fontSize: 10, color: c.faint, fontWeight: 600 }}>{v.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Signups Chart (Simple bar chart) */}
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Daily Signups (30 days)</div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 100 }}>
                  {(analytics.signups || []).map((d, i) => {
                    const max = Math.max(...(analytics.signups || []).map(s => s.count), 1);
                    const h = Math.max(4, (d.count / max) * 90);
                    return <div key={i} title={`${d.date}: ${d.count} signups`} style={{ flex: 1, height: h, background: "linear-gradient(180deg, #6c5ce7, #6c5ce740)", borderRadius: "3px 3px 0 0", cursor: "help", minWidth: 4 }} />;
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                  <span style={{ fontSize: 9, color: c.faint }}>{analytics.signups?.[0]?.date?.slice(5) || ""}</span>
                  <span style={{ fontSize: 9, color: c.faint }}>{analytics.signups?.[analytics.signups.length-1]?.date?.slice(5) || ""}</span>
                </div>
              </div>

              {/* Active Users Chart */}
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Daily Active Users (30 days)</div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 100 }}>
                  {(analytics.active || []).map((d, i) => {
                    const max = Math.max(...(analytics.active || []).map(s => s.count), 1);
                    const h = Math.max(4, (d.count / max) * 90);
                    return <div key={i} title={`${d.date}: ${d.count} active`} style={{ flex: 1, height: h, background: "linear-gradient(180deg, #10b981, #10b98140)", borderRadius: "3px 3px 0 0", cursor: "help", minWidth: 4 }} />;
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                  <span style={{ fontSize: 9, color: c.faint }}>{analytics.active?.[0]?.date?.slice(5) || ""}</span>
                  <span style={{ fontSize: 9, color: c.faint }}>{analytics.active?.[analytics.active.length-1]?.date?.slice(5) || ""}</span>
                </div>
              </div>

              {/* Messages Chart */}
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Daily Messages (30 days)</div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 100 }}>
                  {(analytics.messages || []).map((d, i) => {
                    const max = Math.max(...(analytics.messages || []).map(s => s.count), 1);
                    const h = Math.max(4, (d.count / max) * 90);
                    return <div key={i} title={`${d.date}: ${d.count} msgs`} style={{ flex: 1, height: h, background: "linear-gradient(180deg, #3b82f6, #3b82f640)", borderRadius: "3px 3px 0 0", cursor: "help", minWidth: 4 }} />;
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                  <span style={{ fontSize: 9, color: c.faint }}>{analytics.messages?.[0]?.date?.slice(5) || ""}</span>
                  <span style={{ fontSize: 9, color: c.faint }}>{analytics.messages?.[analytics.messages.length-1]?.date?.slice(5) || ""}</span>
                </div>
              </div>

              {/* Top Active Users */}
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Most Active Users (7 days)</div>
                {(analytics.top_active || []).slice(0, 10).map((u, i) => (
                  <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < 9 ? `1px solid ${c.border}` : "none" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: c.faint, width: 18 }}>{i+1}</span>
                    <div style={{ width: 30, height: 30, borderRadius: 15, background: "linear-gradient(135deg, #6c5ce7, #a23ad1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{u.avatar_emoji || "👤"}</div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: c.text }}>{u.name}</span>
                      <TierBadge plan={u.plan} verified={u.is_verified} size={11} />
                      <div style={{ fontSize: 10, color: c.faint }}>{u.posts_count} posts · {u.dm_count} DMs · ★{u.trust_score}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Top Messagers */}
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Top DM Users (30 days)</div>
                {(analytics.top_messagers || []).map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < (analytics.top_messagers || []).length - 1 ? `1px solid ${c.border}` : "none" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: c.faint, width: 18 }}>{i+1}</span>
                    <div style={{ width: 30, height: 30, borderRadius: 15, background: "linear-gradient(135deg, #3b82f6, #6c5ce7)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{item.user?.avatar_emoji || "👤"}</div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: c.text }}>{item.user?.name || "Unknown"}</span>
                      <TierBadge plan={item.user?.plan} verified={item.user?.is_verified} size={11} />
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#3b82f6" }}>{item.msg_count}</div>
                    <span style={{ fontSize: 10, color: c.faint }}>msgs</span>
                  </div>
                ))}
              </div>
            </>}
          </div>
        )}
        {adminTab === "users" && (<div style={{ background: c.card, borderRadius: 15, border: `1px solid ${c.border}`, padding: 18 }}>
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
    <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: c.text }}>Users ({adminUsers.total || 0})</h3>
    <div style={{ fontSize: 12, color: c.sub }}>Page {adminUsers.current_page || 1} of {adminUsers.last_page || 1}</div>
  </div>
  {(adminUsers.data || []).length === 0 ? <EmptyState icon="👥" title="No users found" /> : (adminUsers.data || []).map((u, i, arr) => (
    <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: i < arr.length - 1 ? `1px solid ${c.border}` : "none" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {u.avatar_url ? <img src={u.avatar_url} style={{ width: 34, height: 34, borderRadius: 17, objectFit: "cover" }} alt="" /> : <div style={{ width: 34, height: 34, borderRadius: 17, background: c.grad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{u.avatar_emoji || "👤"}</div>}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{u.name} <TierBadge plan={u.plan} verified={u.is_verified} size={13} /> {u.is_banned && <span style={{ color: c.red }}>🚫</span>}</div>
          <div style={{ fontSize: 11, color: c.faint }}>Trust: {u.trust_score} · {u.plan || "free"} · {u.posts_count || 0} posts</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
        <select value={u.plan || "free"} onChange={async e => { const res = await api(`/admin/users/${u.id}/plan`, { method: "POST", body: JSON.stringify({ plan: e.target.value }) }); if (res.ok) { flash(res.data?.message || "Plan updated"); loadAdminUsers(adminUsers.current_page || 1); } else flash("Failed", "error"); }} style={{ padding: "4px 8px", borderRadius: 7, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 11, fontWeight: 600, outline: "none" }}>
          <option value="free">Free</option><option value="premium">Premium</option><option value="elite">Elite</option>
        </select>
        <Btn small onClick={async () => { const pw = prompt("New password for " + u.name + " (min 6 chars):"); if (pw && pw.length >= 6) { const res = await api(`/admin/users/${u.id}/reset-password`, { method: "POST", body: JSON.stringify({ password: pw }) }); if (res.ok) flash(res.data?.message || "Password reset!"); else flash("Failed", "error"); } else if (pw) flash("Min 6 characters", "error"); }}>🔑</Btn>
        {u.is_banned ? <Btn small onClick={async () => { await api(`/admin/users/${u.id}/unban`, { method: "POST" }); flash("Unbanned"); loadAdminUsers(adminUsers.current_page || 1); }}>Unban</Btn> : <Btn danger small onClick={async () => { await api(`/admin/users/${u.id}/ban`, { method: "POST", body: JSON.stringify({ reason: "Admin action" }) }); flash("Banned", "error"); loadAdminUsers(adminUsers.current_page || 1); }}>Ban</Btn>}
      </div>
    </div>
  ))}
  {(adminUsers.last_page || 1) > 1 && (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${c.border}`, flexWrap: "wrap" }}>
      <button onClick={() => loadAdminUsers((adminUsers.current_page || 1) - 1)} disabled={(adminUsers.current_page || 1) <= 1} style={{ padding: "7px 14px", borderRadius: 9, border: `1px solid ${c.border}`, background: c.card, color: c.text, fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: (adminUsers.current_page || 1) <= 1 ? 0.4 : 1 }}>← Prev</button>
      {Array.from({ length: adminUsers.last_page || 1 }, (_, i) => i + 1).map(pg => (
        <button key={pg} onClick={() => loadAdminUsers(pg)} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${pg === (adminUsers.current_page || 1) ? c.pri : c.border}`, background: pg === (adminUsers.current_page || 1) ? c.priBg : c.card, color: pg === (adminUsers.current_page || 1) ? c.priTxt : c.sub, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{pg}</button>
      ))}
      <button onClick={() => loadAdminUsers((adminUsers.current_page || 1) + 1)} disabled={(adminUsers.current_page || 1) >= (adminUsers.last_page || 1)} style={{ padding: "7px 14px", borderRadius: 9, border: `1px solid ${c.border}`, background: c.card, color: c.text, fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: (adminUsers.current_page || 1) >= (adminUsers.last_page || 1) ? 0.4 : 1 }}>Next →</button>
    </div>
  )}
</div>)}

        {/* ── VERIFICATION TAB ── */}
        {adminTab === "verify" && (
          <div style={{ background: c.card, borderRadius: 15, border: `1px solid ${c.border}`, padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: c.text }}>🛡️ Verification Requests</h3>
              <select value={verifFilter} onChange={e => setVerifFilter(e.target.value)} style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 12, fontWeight: 600, outline: "none" }}>
                <option value="pending">⏳ Pending</option>
                <option value="approved">✅ Approved</option>
                <option value="rejected">❌ Rejected</option>
                <option value="all">All</option>
              </select>
            </div>

            {verifRequests.length === 0 ? (
              <EmptyState icon="🛡️" title={`No ${verifFilter} requests`} desc={verifFilter === "pending" ? "All caught up!" : ""} />
            ) : (
              verifRequests.map((vr, i) => (
                <div key={vr.id} style={{ padding: "14px 0", borderBottom: i < verifRequests.length - 1 ? `1px solid ${c.border}` : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    {vr.user?.avatar_url ? <img src={vr.user.avatar_url} style={{ width: 40, height: 40, borderRadius: 20, objectFit: "cover" }} alt="" /> : <div style={{ width: 40, height: 40, borderRadius: 20, background: c.grad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{vr.user?.avatar_emoji || "👤"}</div>}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: c.text }}>{vr.user?.name || "Unknown"}</div>
                      <div style={{ fontSize: 11, color: c.faint }}>Trust: {vr.user?.trust_score || 0} · Submitted {timeAgo(vr.created_at)}</div>
                    </div>
                    <span style={{ padding: "3px 9px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: vr.status === "pending" ? c.goldBg : vr.status === "approved" ? c.grnBg : c.redBg, color: vr.status === "pending" ? c.goldTxt : vr.status === "approved" ? c.grnTxt : c.redTxt }}>{vr.status}</span>
                  </div>
                  <div style={{ background: c.input, borderRadius: 10, padding: 12, marginBottom: 10 }}>
                    {vr.instagram && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 14 }}>📸</span>
                        <a href={`https://instagram.com/${vr.instagram}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: c.priTxt, fontWeight: 600, textDecoration: "none" }}>@{vr.instagram}</a>
                      </div>
                    )}
                    {vr.twitter && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 14 }}>🐦</span>
                        <a href={`https://x.com/${vr.twitter}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: c.priTxt, fontWeight: 600, textDecoration: "none" }}>@{vr.twitter}</a>
                      </div>
                    )}
                    {vr.facebook && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14 }}>📘</span>
                        <a href={vr.facebook.startsWith("http") ? vr.facebook : `https://facebook.com/${vr.facebook}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: c.priTxt, fontWeight: 600, textDecoration: "none" }}>@{vr.facebook}</a>
                      </div>
                    )}
                  </div>
                  {vr.status === "pending" && (
                    <div>
                      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                        <Btn primary small style={{ flex: 1 }} onClick={() => approveVerif(vr.id)}>✓ Approve</Btn>
                        <Btn danger small style={{ flex: 1 }} onClick={() => rejectVerif(vr.id)}>✗ Reject</Btn>
                      </div>
                      <input
                        style={{ width: "100%", padding: "8px 12px", borderRadius: 9, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 12, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                        placeholder="Rejection reason (required to reject)..."
                        value={rejectNote[vr.id] || ""}
                        onChange={e => setRejectNote({ ...rejectNote, [vr.id]: e.target.value })}
                      />
                    </div>
                  )}
                  {vr.status !== "pending" && vr.admin_note && (
                    <div style={{ fontSize: 12, color: c.faint, fontStyle: "italic" }}>Note: {vr.admin_note}</div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {adminTab === "reports" && (<div style={{ background: c.card, borderRadius: 15, border: `1px solid ${c.border}`, padding: 18 }}><h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 14px", color: c.text }}>Reports</h3>{adminReports.length === 0 ? <EmptyState icon="⚠️" title="No reports" desc="All clear!" /> : adminReports.map((r, i) => (<div key={r.id} style={{ padding: "12px 0", borderBottom: i < adminReports.length - 1 ? `1px solid ${c.border}` : "none" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontSize: 13, color: c.text }}><b>{r.reporter?.name || "User"}</b> reported content</div><div style={{ fontSize: 12, color: c.sub, marginTop: 2 }}>Reason: {r.reason} · {timeAgo(r.created_at)}</div></div><span style={{ padding: "3px 9px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: r.status === "pending" ? c.goldBg : c.grnBg, color: r.status === "pending" ? c.goldTxt : c.grnTxt }}>{r.status}</span></div>{r.status === "pending" && (<div style={{ display: "flex", gap: 6, marginTop: 8 }}><Btn primary small onClick={async () => { await api(`/admin/reports/${r.id}/resolve`, { method: "POST", body: JSON.stringify({ status: "resolved", note: "Reviewed" }) }); flash("Resolved"); setAdminTab("overview"); setTimeout(() => setAdminTab("reports"), 100); }}>Resolve</Btn><Btn small onClick={async () => { await api(`/admin/reports/${r.id}/resolve`, { method: "POST", body: JSON.stringify({ status: "dismissed", note: "Dismissed" }) }); flash("Dismissed"); setAdminTab("overview"); setTimeout(() => setAdminTab("reports"), 100); }}>Dismiss</Btn></div>)}</div>))}</div>)}
        {adminTab === "content" && (<div style={{ background: c.card, borderRadius: 15, border: `1px solid ${c.border}`, padding: 18 }}><h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 14px", color: c.text }}>Content Moderation</h3><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 14 }}><Stat icon="📝" label="Total Posts" value={posts.length} color={c.pri} /><Stat icon="🚫" label="Flagged" value="0" color={c.red} /></div>{posts.slice(0, 5).map(p => (<div key={p.id} style={{ padding: 12, borderRadius: 11, background: c.input, border: `1px solid ${c.border}`, marginBottom: 7 }}><div style={{ fontSize: 12, color: c.sub, marginBottom: 5 }}><b>{p.authorName || "User"}</b> · {p.time}</div><div style={{ fontSize: 13, color: c.text, marginBottom: 8 }}>{p.content.slice(0, 100)}{p.content.length > 100 ? "..." : ""}</div><div style={{ display: "flex", gap: 6 }}><Btn small style={{ background: c.grnBg, color: c.grnTxt, border: "none" }} onClick={async () => { await api(`/admin/posts/${p.id}/moderate`, { method: "POST", body: JSON.stringify({ action: "approve" }) }); flash("Approved"); }}>Approve</Btn><Btn danger small onClick={async () => { await api(`/admin/posts/${p.id}/moderate`, { method: "POST", body: JSON.stringify({ action: "delete" }) }); flash("Removed", "error"); loadPosts(topic); }}>Remove</Btn></div></div>))}{posts.length === 0 && <EmptyState icon="📝" title="No posts yet" />}</div>)}
                {adminTab === "events" && <AdminEvents />}
        {adminTab === "stickers" && <AdminStickers />}
        {adminTab === "movies" && <AdminMovies />}
        {adminTab === "revenue" && (<><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 14 }}><Stat icon="💰" label="Revenue" value={`KES ${(ad.revenue || 0).toLocaleString()}`} color={c.grn} /><Stat icon="👑" label="Premium" value={ad.premium_users || 0} color="#8b5cf6" /></div></>)}
        {adminTab === "settings" && (<div style={{ background: c.card, borderRadius: 15, border: `1px solid ${c.border}`, padding: 18 }}><h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 14px", color: c.text }}>Community Settings</h3>{[{ l: "Require email verification", d: "New users must verify email", on: true }, { l: "Auto-moderate posts", d: "AI content filtering", on: true }, { l: "Allow guest browsing", d: "Non-members can view public posts", on: false }, { l: "Invite-only registration", d: "Require invitation code to join", on: false }, { l: "Maintenance mode", d: "Temporarily disable the community", on: false }].map((item, i, arr) => (<div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: i < arr.length - 1 ? `1px solid ${c.border}` : "none" }}><div><div style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{item.l}</div><div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>{item.d}</div></div><Toggle on={item.on} onToggle={() => { }} /></div>))}</div>)}
      </div>
    </Shell>
  );
};